import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGallery } from '../store/gallerySlice';
import { getApiBaseUrl, resolveMediaUrl } from '../utils/media';

const baseStyles = {
  shell: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '24px 16px',
    fontFamily: '"Segoe UI", Arial, sans-serif',
    color: '#0f172a',
  },
  header: {
    textAlign: 'center',
    marginBottom: 40,
    padding: '0 16px',
  },
  title: {
    margin: 0,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  subtitle: {
    marginTop: 12,
    color: '#64748b',
    fontSize: 18,
    lineHeight: 1.6,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 32,
  },
  filterBtn: {
    padding: '8px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: 20,
    background: '#fff',
    color: '#475569',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterBtnActive: {
    background: '#2563eb',
    color: '#fff',
    border: '1px solid #2563eb',
  },
  grid: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.08)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  image: {
    width: '100%',
    height: 220,
    objectFit: 'cover',
  },
  imagePlaceholder: {
    width: '100%',
    height: 220,
    background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    fontSize: 48,
  },
  cardBody: {
    padding: 16,
  },
  cardTitle: {
    margin: '0 0 8px',
    fontSize: 16,
    fontWeight: 600,
    color: '#0f172a',
  },
  cardCategory: {
    display: 'inline-block',
    padding: '2px 8px',
    background: '#f1f5f9',
    color: '#64748b',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 500,
  },
  cardDescription: {
    margin: '8px 0 0',
    fontSize: 14,
    color: '#64748b',
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    maxWidth: 900,
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalImage: {
    width: '100%',
    maxHeight: '70vh',
    objectFit: 'contain',
  },
  modalBody: {
    padding: 20,
  },
  closeBtn: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 40,
    height: 40,
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.9)',
    color: '#0f172a',
    fontSize: 20,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loading: {
    textAlign: 'center',
    padding: 60,
    color: '#64748b',
    fontSize: 16,
  },
  error: {
    textAlign: 'center',
    padding: 40,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 12,
    color: '#b91c1c',
  },
  empty: {
    textAlign: 'center',
    padding: 60,
    color: '#64748b',
    fontSize: 16,
  },
};

function injectStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('gallery-responsive-styles')) return;

  const style = document.createElement('style');
  style.id = 'gallery-responsive-styles';
  style.textContent = `
    @media (max-width: 640px) {
      .gallery-shell { padding: 14px !important; }
      .gallery-title { font-size: 28px !important; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
    }
    @media (max-width: 400px) {
      .gallery-grid { grid-template-columns: 1fr !important; }
    }
    .gallery-card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(15, 23, 42, 0.12); }
    .gallery-filter:hover { background: #f1f5f9; }
    .gallery-filter.active { background: #2563eb; color: #fff; border-color: #2563eb; }
  `;
  document.head.appendChild(style);
}

function LoadingSpinner() {
  return <div style={baseStyles.loading}>Loading gallery...</div>;
}

function GalleryCard({ image, onClick, apiBaseUrl }) {
  const [hover, setHover] = useState(false);

  return (
    <div
      className="gallery-card"
      style={{
        ...baseStyles.card,
        transform: hover ? 'translateY(-4px)' : 'none',
        boxShadow: hover ? '0 12px 40px rgba(15, 23, 42, 0.12)' : '0 4px 20px rgba(15, 23, 42, 0.08)',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onClick(image)}
    >
      {image.image_url ? (
        <img style={baseStyles.image} src={resolveMediaUrl(image.image_url, apiBaseUrl)} alt={image.title} loading="lazy" />
      ) : (
        <div style={baseStyles.imagePlaceholder}>
          <i className="fas fa-image" />
        </div>
      )}
      <div style={baseStyles.cardBody}>
        <h3 style={baseStyles.cardTitle}>{image.title}</h3>
        {image.category && (
          <span style={baseStyles.cardCategory}>{image.category}</span>
        )}
        {image.description && (
          <p style={baseStyles.cardDescription}>{image.description}</p>
        )}
      </div>
    </div>
  );
}

function ImageModal({ image, onClose, apiBaseUrl }) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!image) return null;

  return (
    <div style={baseStyles.modal} onClick={onClose}>
      <div style={baseStyles.modalContent} onClick={(e) => e.stopPropagation()}>
        {image.image_url && (
          <img style={baseStyles.modalImage} src={resolveMediaUrl(image.image_url, apiBaseUrl)} alt={image.title} />
        )}
        <div style={baseStyles.modalBody}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>{image.title}</h2>
          {image.category && (
            <span style={{ ...baseStyles.cardCategory, marginBottom: 12, display: 'inline-block' }}>
              {image.category}
            </span>
          )}
          {image.description && (
            <p style={{ margin: '12px 0 0', color: '#64748b', lineHeight: 1.6 }}>{image.description}</p>
          )}
        </div>
      </div>
      <button style={baseStyles.closeBtn} onClick={onClose}>
        <i className="fas fa-times" />
      </button>
    </div>
  );
}

export function GalleryPage({ apiBaseUrl = getApiBaseUrl(), title = 'Our Gallery', subtitle = 'Explore our hospital facilities and events' }) {
  const dispatch = useDispatch();
  const { items: images, status, error } = useSelector((state) => state.gallery);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const loading = status === 'loading' && images.length === 0;

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    dispatch(fetchGallery({ apiBaseUrl }));
  }, [apiBaseUrl, dispatch]);

  const categories = useMemo(() => {
    const cats = new Set(['all']);
    images.forEach((img) => {
      if (img.category) cats.add(img.category);
    });
    return Array.from(cats);
  }, [images]);

  const filteredImages = useMemo(() => {
    if (selectedCategory === 'all') return images;
    return images.filter((img) => img.category === selectedCategory);
  }, [images, selectedCategory]);

  return (
    <div style={baseStyles.shell} className="gallery-shell">
      <header style={baseStyles.header}>
        <h1 className="gallery-title" style={baseStyles.title}>{title}</h1>
        {subtitle && <p style={baseStyles.subtitle}>{subtitle}</p>}
      </header>

      {error && <div style={baseStyles.error}>{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div style={baseStyles.filters}>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`gallery-filter ${selectedCategory === cat ? 'active' : ''}`}
                style={{
                  ...baseStyles.filterBtn,
                  ...(selectedCategory === cat ? baseStyles.filterBtnActive : {}),
                }}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat === 'all' ? 'All' : cat}
              </button>
            ))}
          </div>

          {filteredImages.length > 0 ? (
            <div style={baseStyles.grid} className="gallery-grid">
              {filteredImages.map((image) => (
                <GalleryCard
                  key={image.id}
                  image={image}
                  onClick={setSelectedImage}
                  apiBaseUrl={apiBaseUrl}
                />
              ))}
            </div>
          ) : (
            <div style={baseStyles.empty}>
              <i className="fas fa-images" style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
              <div>No images available in this category.</div>
            </div>
          )}
        </>
      )}

      {selectedImage && (
        <ImageModal image={selectedImage} onClose={() => setSelectedImage(null)} apiBaseUrl={apiBaseUrl} />
      )}
    </div>
  );
}

export default GalleryPage;
